/*
BASE64-JS plugin (MIT License)
https://github.com/beatgammit/base64-js
does basic base64 encoding/decoding in pure JS.
*/

export class Base64js {
    private static __lookup: any[] = [];
    private static __revLookup: any[] = [];
    private static _arr: any = typeof Uint8Array !== "undefined" ? Uint8Array : Array;
    private static _code = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

    private static _isInitialized: boolean = false;

    private static get _lookup(): any[] {
        if (!Base64js._isInitialized)
            Base64js._initialize();
        return Base64js.__lookup;
    }

    private static get _revLookup(): any[] {
        if (!Base64js._isInitialized)
            Base64js._initialize();
        return Base64js.__revLookup;
    }

    private static _initialize(): void {
        for (let i = 0, len = this._code.length; i < len; ++i) {
            Base64js.__lookup[i] = this._code[i];
            Base64js.__revLookup[this._code.charCodeAt(i)] = i;
        }

        Base64js.__revLookup["-".charCodeAt(0)] = 62;
        Base64js.__revLookup["_".charCodeAt(0)] = 63;
    }

    private static _placeHoldersCount(b64: any): 0 | 1 | 2 {
        const len = b64.length;
        if (len % 4 > 0) {
            throw new Error("Invalid string. Length must be a multiple of 4");
        }

        // the number of equal signs (place holders)
        // if there are two placeholders, than the two characters before it
        // represent one byte
        // if there is only one, then the three characters before it represent 2 bytes
        // this is just a cheap hack to not do indexOf twice
        return b64[len - 2] === "=" ? 2 : b64[len - 1] === "=" ? 1 : 0;
    }

    //private static byteLength(b64: any) {
    //    // base64 is 4/3 + up to two characters of the original data
    //    return (b64.length * 3 / 4) - this.placeHoldersCount(b64)
    //}

    public static toByteArray(b64: any): any {
        const len = b64.length;
        const placeHolders = this._placeHoldersCount(b64);
        const arr = new this._arr((len * 3 / 4) - placeHolders);

        // if there are placeholders, only get up to the last complete 4 chars
        const l = placeHolders > 0 ? len - 4 : len;

        let L = 0;

        let i, tmp;
        for (i = 0; i < l; i += 4) {
            tmp = (Base64js._revLookup[b64.charCodeAt(i)] << 18) | (Base64js._revLookup[b64.charCodeAt(i + 1)] << 12) | (Base64js._revLookup[b64.charCodeAt(i + 2)] << 6) | Base64js._revLookup[b64.charCodeAt(i + 3)];
            arr[L++] = (tmp >> 16) & 0xFF;
            arr[L++] = (tmp >> 8) & 0xFF;
            arr[L++] = tmp & 0xFF;
        }

        if (placeHolders === 2) {
            tmp = (Base64js._revLookup[b64.charCodeAt(i)] << 2) | (Base64js._revLookup[b64.charCodeAt(i + 1)] >> 4);
            arr[L++] = tmp & 0xFF;
        } else if (placeHolders === 1) {
            tmp = (Base64js._revLookup[b64.charCodeAt(i)] << 10) | (Base64js._revLookup[b64.charCodeAt(i + 1)] << 4) | (Base64js._revLookup[b64.charCodeAt(i + 2)] >> 2);
            arr[L++] = (tmp >> 8) & 0xFF;
            arr[L++] = tmp & 0xFF;
        }

        return arr;
    }

    private static _tripletToBase64(num: any): any {
        return Base64js._lookup[num >> 18 & 0x3F] + Base64js._lookup[num >> 12 & 0x3F] + Base64js._lookup[num >> 6 & 0x3F] + Base64js._lookup[num & 0x3F];
    }

    private static _encodeChunk(uint8: any, start: any, end: any): string {
        let tmp;
        const output: any[] = [];
        for (let i = start; i < end; i += 3) {
            tmp = (uint8[i] << 16) + (uint8[i + 1] << 8) + (uint8[i + 2]);
            output.push(Base64js._tripletToBase64(tmp));
        }
        return output.join("");
    }

    public static fromByteArray(uint8: any): string {
        let tmp;
        const len = uint8.length;
        const extraBytes = len % 3; // if we have 1 byte left, pad 2 bytes
        let output = "";
        const parts: string[] = [];
        const maxChunkLength = 16383; // must be multiple of 3

        // go through the array every three bytes, we'll deal with trailing stuff later
        for (let i = 0, len2 = len - extraBytes; i < len2; i += maxChunkLength) {
            parts.push(Base64js._encodeChunk(uint8, i, (i + maxChunkLength) > len2 ? len2 : (i + maxChunkLength)));
        }

        // pad the end with zeros, but make sure to not forget the extra bytes
        if (extraBytes === 1) {
            tmp = uint8[len - 1];
            output += Base64js._lookup[tmp >> 2];
            output += Base64js._lookup[(tmp << 4) & 0x3F];
            output += "==";
        } else if (extraBytes === 2) {
            tmp = (uint8[len - 2] << 8) + (uint8[len - 1]);
            output += Base64js._lookup[tmp >> 10];
            output += Base64js._lookup[(tmp >> 4) & 0x3F];
            output += Base64js._lookup[(tmp << 2) & 0x3F];
            output += "=";
        }

        parts.push(output);

        return parts.join("");
    }
}
